import { hydra } from '@tpluscode/rdf-ns-builders';
function satisfies(criteria, value, actualCheck) {
    if (!criteria || !value) {
        return true;
    }
    return actualCheck(criteria, value);
}
function satisfiesMethod(criteria, operation) {
    return satisfies(criteria.byMethod, operation.method, (expected, actual) => {
        if (typeof expected === 'string') {
            return expected.toUpperCase() === actual.toUpperCase();
        }
        return expected(actual);
    });
}
function matchClass(expected, actual) {
    if (typeof expected === 'string') {
        return actual.id === expected;
    }
    if (typeof expected === 'function') {
        return expected(actual);
    }
    return expected.id === actual.id;
}
function satisfiesExpects(criteria, operation) {
    return satisfies(criteria.expecting, operation.expects, matchClass);
}
function satisfiesReturns(criteria, operation) {
    return satisfies(criteria.returning, operation.returns, matchClass);
}
function satisfiesTypeOrId(criteria, operation) {
    return satisfies(criteria.bySupportedOperation, operation.supportedOperation, (expected, actual) => {
        if (typeof expected === 'string') {
            return actual.id === expected || actual.types.contains(expected);
        }
        return expected(actual);
    });
}
function createMatcher(operation) {
    return (criteria) => {
        if (!criteria.byMethod) {
            criteria.byMethod = method => method.toUpperCase() !== 'GET';
        }
        return satisfiesReturns(criteria, operation) &&
            satisfiesExpects(criteria, operation) &&
            satisfiesMethod(criteria, operation) &&
            satisfiesTypeOrId(criteria, operation);
    };
}
const toResourceWithOperations = (stopConditions) => {
    return (resources, [prop, value]) => {
        if (stopConditions.excludedProperties.includes(prop)) {
            return resources;
        }
        let array = Array.isArray(value) ? value : [value];
        const moreResources = array
            .filter(resource => resource !== null &&
            typeof resource === 'object' &&
            'getOperationsDeep' in resource);
        return [...resources, ...moreResources];
    };
};
export function OperationFinder(Base) {
    return class OperationFinderMixinClass extends Base {
        getOperationsDeep(stopConditions = { excludedProperties: [hydra.member.value] }, previousResources = []) {
            return Object.entries(this)
                .reduce(toResourceWithOperations(stopConditions), [])
                .reduce((operations, resource, index, resources) => {
                if (previousResources.includes(resource))
                    return operations;
                const currentlyVisited = [...resources, ...previousResources, this];
                return [
                    ...operations,
                    ...resource.getOperationsDeep(stopConditions, currentlyVisited),
                ];
            }, this.operations);
        }
        findOperations(...criteria) {
            return this.__filterOperations(this.operations, criteria);
        }
        findOperationsDeep(stopConditionOrCriteria, ...moreCriteria) {
            if (!stopConditionOrCriteria) {
                return this.__filterOperations(this.getOperationsDeep());
            }
            if ('excludedProperties' in stopConditionOrCriteria) {
                return this.__filterOperations(this.getOperationsDeep(stopConditionOrCriteria), moreCriteria);
            }
            return this.__filterOperations(this.getOperationsDeep(), [stopConditionOrCriteria, ...moreCriteria]);
        }
        __filterOperations(operations, criteria = []) {
            let actualCriteria = [...criteria];
            if (actualCriteria.length === 0) {
                actualCriteria.push({});
            }
            return operations.reduce((operations, operation) => {
                if (actualCriteria.find(createMatcher(operation))) {
                    operations.push(operation);
                }
                return operations;
            }, []);
        }
    };
}
//# sourceMappingURL=OperationFinder.js.map